import type {ComponentPropsWithRef} from 'react';
import React from 'react';

// Forward reference — will resolve once Row is migrated to ts-packages
import {Row} from './row';

import {getRowData} from './utils';

export type HeadProps = ComponentPropsWithRef<'thead'> & {
    /**
     * Custom header height passed down to cells
     */
    height?: number,
    /**
     * Default props to spread onto each header cell.
     * Useful for a11y attributes, e.g. `cellDefaults={{ scope: 'col' }}`.
     * Consumer-provided props on individual cells take priority.
     */
    cellDefaults?: Record<string, unknown>,
    /**
     * Mix for Head
     * @deprecated Use `className` instead. It will be removed in 6 months 03-03-2026.
     */
    mix?: string,
};

/**
 * Table header section. Prefer wrapping cells in an explicit `<Row>`:
 *
 * ```tsx
 * <Head>
 *   <Row>
 *     <Cell>Company</Cell>
 *     <Cell>Amount</Cell>
 *   </Row>
 * </Head>
 * ```
 *
 * Passing bare `<Cell>` children directly (without a `<Row>`) is deprecated
 * and will be removed in 6 months 03-03-2026.
 */
export const Head = ({
    children,
    height,
    cellDefaults,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    mix,
    className,
    ref,
    ...rest
}: HeadProps) => {
    const {children: mappedChildren, wrappedCells} = getRowData({children, height, cellDefaults}, Row);
    const mergedClassName = [mix, className].filter(Boolean).join(' ') || undefined;

    return (
        <thead
            {...rest}
            ref={ref}
            className={mergedClassName}>
            {mappedChildren}
            {wrappedCells}
        </thead>
    );
};
